import React, { useEffect, useState, useCallback, useRef } from "react";
import { Box, Button, Grid, Tabs, Tab } from "@mui/material";
import { useSearchParams, useLocation } from "react-router-dom";
import { IconBoltType, IconFilterSvg } from "../../common/utils/icons";
import IssueListDataGrid from "../../components/DataGrids/IssueListDataGrid";
import ButtonSwitcher from "../../components/ButtonSwitcher/ButtonSwitcher";
import useFetchAllPaginatedIssues from "../../hooks/useFetchAllPaginatedIssues";
import Loader from "../../components/Molecules/Loader/Loader";
import SearchFilterBar from "../../components/Molecules/FilterSearch/SearchFilterBar";
import { useAuth } from "../../contexts/AuthContext";
import { useRowsPerPage } from "../../hooks/useRowsPerPage";
import useFetchAllClassifications from "../../hooks/useFetchAllClassifications";
import { useClassificationContext } from "../../contexts/ClassificationContext";

// Type definitions for better type safety
type TabType = "veIssues" | "rqIssues";
type PageType = "execution" | "validation" | "ruleResearch" | "genericResearch";
type PageTypeByTab = {
  veIssues: "execution" | "validation";
  rqIssues: "ruleResearch" | "genericResearch";
};

// Centralized Filter Configuration for VE Issues (Validation/Execution)
// Note: Field names match the internal state. Different API endpoints are used:
// - Execution: /config/issues/pages (uses rule_id)
// - Validation: /config/issues/pages/validation (uses resource_id)
const VE_ISSUES_FILTER_CONFIG = {
  DEFAULT_STATE: {
    view_type: "execution" as "execution" | "validation", // Track current view
    domain_id: null,
    rule_id: null, // For execution view
    resource_id: null, // For validation view
    start_date: null,
    end_date: null,
    assigned_user: "",
    assigned_by: "",
    execution_id: "",
    status: "",
    classification: "",
    reason: "",
    issue_classification_id: null as number | null,
    issue_reason_id: null as number | null,
  },

  get URL_FIELDS() {
    return Object.keys(this.DEFAULT_STATE);
  },

  FIELD_TYPES: {
    ID_FIELDS: [
      "domain_id",
      "rule_id",
      "resource_id",
      "issue_classification_id",
      "issue_reason_id",
    ],
    STRING_FIELDS: [
      "view_type",
      "assigned_user",
      "assigned_by",
      "execution_id",
      "status",
      "classification",
      "reason",
    ],
    DATE_FIELDS: ["start_date", "end_date"],
  },
} as const;

// Centralized Filter Configuration for RQ Issues (Research Query Issues)
// Note: Field names match the internal state. Different views:
// - Rule Research: uses rule_id or generic_research_id
// - Generic Research: uses generic_research_id and query_name
const RQ_ISSUES_FILTER_CONFIG = {
  DEFAULT_STATE: {
    view_type: "ruleResearch" as "ruleResearch" | "genericResearch", // Track current view
    domain_id: null,
    generic_research_id: "",
    query_name: "",
    start_date: null,
    end_date: null,
    assigned_user: "",
    assigned_by: "",
    execution_id: "",
    status: "",
  },

  get URL_FIELDS() {
    return Object.keys(this.DEFAULT_STATE);
  },

  FIELD_TYPES: {
    ID_FIELDS: ["domain_id"],
    STRING_FIELDS: [
      "view_type",
      "generic_research_id",
      "query_name",
      "assigned_user",
      "assigned_by",
      "execution_id",
      "status",
    ],
    DATE_FIELDS: ["start_date", "end_date"],
  },
} as const;

const AllIssueList = () => {
  const { hasPermission } = useAuth();

  // Fetch classifications once and store in shared context (same pattern as TagAndSystemProvider)
  const { setAllClassifications, setIsClassificationsLoading } =
    useClassificationContext();
  const [fetchedClassifications] = useFetchAllClassifications({
    setIsLoading: setIsClassificationsLoading,
  });

  useEffect(() => {
    if (fetchedClassifications.length > 0) {
      setAllClassifications(fetchedClassifications);
    }
  }, [fetchedClassifications, setAllClassifications]);
  const canEditRuleExecutionIssues = hasPermission(
    "rule_execution_issues",
    "edit"
  );
  const canEditValidationIssues = hasPermission("validation_issues", "edit");
  const canEditResearchQueryIssues = hasPermission(
    "research_query_execution_issues",
    "edit"
  );

  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();

  // Refs for tracking state to prevent circular updates (for veIssues tab)
  const isUpdatingFromURL = useRef(false);
  const isInitialMount = useRef(true);

  // Refs for tracking state to prevent circular updates (for rqIssues tab)
  const isUpdatingFromURLRQ = useRef(false);
  const isInitialMountRQ = useRef(true);

  // Function to serialize filter state to URL parameters (for veIssues tab only)
  const serializeFiltersToURL = useCallback(
    (filterData: any, viewType: "execution" | "validation") => {
      const params = new URLSearchParams();

      // Always include view_type
      params.set("view_type", viewType);

      VE_ISSUES_FILTER_CONFIG.URL_FIELDS.forEach((field) => {
        // Skip view_type as it's already set
        if (field === "view_type") {
          return;
        }

        // Skip rule_id for validation view, skip resource_id for execution view
        if (
          (viewType === "validation" && field === "rule_id") ||
          (viewType === "execution" && field === "resource_id")
        ) {
          return;
        }

        const value = filterData[field];

        // Skip null, undefined, or empty string values
        if (value === null || value === undefined || value === "") {
          return;
        }

        // Handle ID fields
        if (
          (
            VE_ISSUES_FILTER_CONFIG.FIELD_TYPES.ID_FIELDS as readonly string[]
          ).includes(field)
        ) {
          params.set(field, String(value));
        }
        // Handle date fields
        else if (
          (
            VE_ISSUES_FILTER_CONFIG.FIELD_TYPES.DATE_FIELDS as readonly string[]
          ).includes(field)
        ) {
          // Convert Date object to YYYY-MM-DD format
          if (value instanceof Date) {
            const year = value.getFullYear();
            const month = String(value.getMonth() + 1).padStart(2, "0");
            const day = String(value.getDate()).padStart(2, "0");
            params.set(field, `${year}-${month}-${day}`);
          } else if (typeof value === "string") {
            params.set(field, value);
          }
        }
        // Handle string fields
        else if (
          (
            VE_ISSUES_FILTER_CONFIG.FIELD_TYPES
              .STRING_FIELDS as readonly string[]
          ).includes(field)
        ) {
          params.set(field, String(value));
        }
      });

      return params;
    },
    []
  );

  // Function to deserialize URL parameters to filter state (for veIssues tab only)
  const deserializeFiltersFromURL = useCallback(() => {
    const filterData: any = {
      ...VE_ISSUES_FILTER_CONFIG.DEFAULT_STATE,
    };

    VE_ISSUES_FILTER_CONFIG.URL_FIELDS.forEach((field) => {
      const urlValue = searchParams.get(field);

      if (urlValue === null || urlValue === "") {
        return;
      }

      // Handle ID fields
      if (
        (
          VE_ISSUES_FILTER_CONFIG.FIELD_TYPES.ID_FIELDS as readonly string[]
        ).includes(field)
      ) {
        const numValue = parseInt(urlValue, 10);
        if (!isNaN(numValue)) {
          filterData[field] = numValue;
        }
      }
      // Handle date fields
      else if (
        (
          VE_ISSUES_FILTER_CONFIG.FIELD_TYPES.DATE_FIELDS as readonly string[]
        ).includes(field)
      ) {
        // Parse YYYY-MM-DD format to Date object
        const dateValue = new Date(urlValue);
        if (!isNaN(dateValue.getTime())) {
          filterData[field] = dateValue;
        }
      }
      // Handle string fields (including view_type)
      else if (
        (
          VE_ISSUES_FILTER_CONFIG.FIELD_TYPES.STRING_FIELDS as readonly string[]
        ).includes(field)
      ) {
        filterData[field] = urlValue;
      }
    });

    return filterData;
  }, [searchParams]);

  // Function to get initial filter state from URL or default (for veIssues tab only)
  const getInitialFilterState = useCallback(() => {
    // Check if there are any filter parameters in the URL
    const hasURLParams = VE_ISSUES_FILTER_CONFIG.URL_FIELDS.some((field) =>
      searchParams.has(field)
    );

    if (hasURLParams) {
      // Initialize from URL parameters
      return deserializeFiltersFromURL();
    }

    // Use default state
    return { ...VE_ISSUES_FILTER_CONFIG.DEFAULT_STATE };
  }, [searchParams, deserializeFiltersFromURL]);

  // Function to get initial view type from URL or default (for veIssues)
  const getInitialViewType = useCallback((): "execution" | "validation" => {
    const urlViewType = searchParams.get("view_type");
    if (urlViewType === "validation" || urlViewType === "execution") {
      return urlViewType;
    }
    return "execution"; // Default to execution
  }, [searchParams]);

  // ========== RQ Issues URL Synchronization Functions ==========

  // Function to serialize filter state to URL parameters (for rqIssues tab only)
  const serializeRQFiltersToURL = useCallback(
    (filterData: any, viewType: "ruleResearch" | "genericResearch") => {
      const params = new URLSearchParams();

      // Always include view_type
      params.set("view_type", viewType);

      RQ_ISSUES_FILTER_CONFIG.URL_FIELDS.forEach((field) => {
        // Skip view_type as it's already set
        if (field === "view_type") {
          return;
        }

        const value = filterData[field];

        // Skip null, undefined, or empty string values
        if (value === null || value === undefined || value === "") {
          return;
        }

        // Handle ID fields
        if (
          (
            RQ_ISSUES_FILTER_CONFIG.FIELD_TYPES.ID_FIELDS as readonly string[]
          ).includes(field)
        ) {
          params.set(field, String(value));
        }
        // Handle date fields
        else if (
          (
            RQ_ISSUES_FILTER_CONFIG.FIELD_TYPES.DATE_FIELDS as readonly string[]
          ).includes(field)
        ) {
          // Convert Date object to YYYY-MM-DD format
          if (value instanceof Date) {
            const year = value.getFullYear();
            const month = String(value.getMonth() + 1).padStart(2, "0");
            const day = String(value.getDate()).padStart(2, "0");
            params.set(field, `${year}-${month}-${day}`);
          } else if (typeof value === "string") {
            params.set(field, value);
          }
        }
        // Handle string fields
        else if (
          (
            RQ_ISSUES_FILTER_CONFIG.FIELD_TYPES
              .STRING_FIELDS as readonly string[]
          ).includes(field)
        ) {
          params.set(field, String(value));
        }
      });

      return params;
    },
    []
  );

  // Function to deserialize URL parameters to filter state (for rqIssues tab only)
  const deserializeRQFiltersFromURL = useCallback(() => {
    const filterData: any = {
      ...RQ_ISSUES_FILTER_CONFIG.DEFAULT_STATE,
    };

    RQ_ISSUES_FILTER_CONFIG.URL_FIELDS.forEach((field) => {
      const urlValue = searchParams.get(field);

      if (urlValue === null || urlValue === "") {
        return;
      }

      // Handle ID fields
      if (
        (
          RQ_ISSUES_FILTER_CONFIG.FIELD_TYPES.ID_FIELDS as readonly string[]
        ).includes(field)
      ) {
        const numValue = parseInt(urlValue, 10);
        if (!isNaN(numValue)) {
          filterData[field] = numValue;
        }
      }
      // Handle date fields
      else if (
        (
          RQ_ISSUES_FILTER_CONFIG.FIELD_TYPES.DATE_FIELDS as readonly string[]
        ).includes(field)
      ) {
        // Parse YYYY-MM-DD format to Date object
        const dateValue = new Date(urlValue);
        if (!isNaN(dateValue.getTime())) {
          filterData[field] = dateValue;
        }
      }
      // Handle string fields (including view_type)
      else if (
        (
          RQ_ISSUES_FILTER_CONFIG.FIELD_TYPES.STRING_FIELDS as readonly string[]
        ).includes(field)
      ) {
        filterData[field] = urlValue;
      }
    });

    return filterData;
  }, [searchParams]);

  // Function to get initial filter state from URL or default (for rqIssues tab only)
  const getInitialRQFilterState = useCallback(() => {
    // Check if there are any filter parameters in the URL
    const hasURLParams = RQ_ISSUES_FILTER_CONFIG.URL_FIELDS.some((field) =>
      searchParams.has(field)
    );

    if (hasURLParams) {
      // Initialize from URL parameters
      return deserializeRQFiltersFromURL();
    }

    // Use default state
    return { ...RQ_ISSUES_FILTER_CONFIG.DEFAULT_STATE };
  }, [searchParams, deserializeRQFiltersFromURL]);

  // Function to get initial view type from URL or default (for rqIssues)
  const getInitialRQViewType = useCallback(():
    | "ruleResearch"
    | "genericResearch" => {
    const urlViewType = searchParams.get("view_type");
    if (urlViewType === "ruleResearch" || urlViewType === "genericResearch") {
      return urlViewType;
    }
    return "ruleResearch"; // Default to ruleResearch
  }, [searchParams]);

  const [userDialogData, setUserDialogData] = useState<{
    status: boolean;
    dialogName: string;
    selectedIds: number[];
  }>({
    status: false,
    dialogName: "",
    selectedIds: [],
  });
  // Centralized selectedIds state management
  const [selectedIds, setSelectedIds] = useState<number[]>([]);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isResultsLoading, setIsResultsLoading] = useState<boolean>(false);

  // Initialize searchFilterData from URL or use defaults (for veIssues tab)
  const [searchFilterData, setSearchFilterData] = useState<any>(() => {
    const initialState = getInitialFilterState();
    // Add additional fields that are not in the filter config but needed by the component
    return {
      ...initialState,
      domain_name: "",
      resource_name: "",
      rule_name: "",
      run_id: null,
      run_name: "",
      date: null,
    };
  });

  // Consolidated page type management - initialize view types from URL
  const [selectedTab, setSelectedTab] = useState<TabType>("veIssues");
  const [pageTypeByTab, setPageTypeByTab] = useState<PageTypeByTab>({
    veIssues: getInitialViewType(), // Initialize from URL or default to "execution"
    rqIssues: getInitialRQViewType(), // Initialize from URL or default to "ruleResearch"
  });
  const [isShowEditIssue, setIsShowEditIssue] = useState<any>(false);

  // Computed current page type based on selected tab
  const currentPageType: PageType = pageTypeByTab[selectedTab];

  const defaultPage = process.env.REACT_APP_DEFAULT_PAGE;
  const { rowsPerPage } = useRowsPerPage();
  const [page, setPage] = useState<number>(
    defaultPage ? parseInt(defaultPage) : 1
  );
  const [pSize, setPSize] = useState<number>(rowsPerPage);

  // Memoize searchFilterData updates to prevent unnecessary re-renders
  const updateSearchFilter = useCallback((newData: any) => {
    setSearchFilterData(newData);
  }, []);

  const fetchedIssuesList: any = useFetchAllPaginatedIssues({
    searchFilterData,
    setIsLoading: setIsResultsLoading,
    currentPageType,
    page,
    pSize,
  });

  // Consolidated page type change handler
  const handlePageTypeChange = useCallback(
    (newPageType: PageType, tabType: TabType) => {
      setPageTypeByTab((prev) => ({
        ...prev,
        [tabType]: newPageType,
      }));

      // Reset search filter data when page type changes
      setSearchFilterData({
        domain_id: null,
        domain_name: "",
        resource_id: null,
        resource_name: "",
        rule_id: null,
        rule_name: "",
        run_id: null,
        run_name: "",
        end_date: null,
        start_date: null,
        date: null,
        assigned_user: "",
        assigned_by: "",
        execution_id: "",
        issue_classification_name: "",
        reason_name: "",
        issue_classification_id: null,
        issue_reason_id: null,
      });
      setPage(1);
      // Clear selected IDs when switcher changes
      setSelectedIds([]);
    },
    []
  );
  // Clear selected IDs when main tab changes
  useEffect(() => {
    setSelectedIds([]);
  }, [selectedTab]);

  useEffect(() => {
    if (currentPageType === "validation" && canEditValidationIssues) {
      setIsShowEditIssue(true);
    } else if (currentPageType === "execution" && canEditRuleExecutionIssues) {
      setIsShowEditIssue(true);
    } else if (
      currentPageType === "ruleResearch" &&
      canEditRuleExecutionIssues
    ) {
      setIsShowEditIssue(true);
    } else if (
      currentPageType === "genericResearch" &&
      canEditResearchQueryIssues
    ) {
      setIsShowEditIssue(true);
    } else {
      setIsShowEditIssue(false);
    }
  }, [
    currentPageType,
    canEditValidationIssues,
    canEditRuleExecutionIssues,
    canEditResearchQueryIssues,
  ]);

  // Ref to track the last processed URL to avoid re-processing the same URL
  const lastProcessedURLRef = useRef(location.search);

  // Ref to track whether the State→URL effect just updated the URL.
  // Used to prevent the URL→State effect from re-processing a URL that was
  // written internally in the same render pass (setSearchParams is async).
  const pendingURLUpdateRef = useRef(false);

  // URL Synchronization: Update URL when filter state or view type changes (State → URL)
  // Only for veIssues tab
  useEffect(() => {
    // Only sync URL for veIssues tab
    if (selectedTab !== "veIssues") {
      return;
    }

    // Skip URL update if we're currently updating from URL to prevent circular updates
    if (isUpdatingFromURL.current) {
      return;
    }

    // Skip on initial mount - URL is already set
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    // Serialize current filter state to URL parameters
    const params = serializeFiltersToURL(
      searchFilterData,
      pageTypeByTab.veIssues
    );

    // Mark that we are about to update the URL so the URL→State effect
    // in the same render pass can skip processing it.
    pendingURLUpdateRef.current = true;

    // Update URL without adding to browser history (replace: true)
    setSearchParams(params, { replace: true });

    // Update the ref so URL→State effect won't re-process this URL change
    lastProcessedURLRef.current = `?${params.toString()}`;
  }, [
    searchFilterData,
    pageTypeByTab.veIssues,
    // selectedTab,
    serializeFiltersToURL,
    setSearchParams,
  ]);

  // URL Synchronization: Update filter state when URL changes (URL → State)
  // Only for veIssues tab
  useEffect(() => {
    // Only sync URL for veIssues tab
    if (selectedTab !== "veIssues") {
      return;
    }

    // If the State→URL effect just updated the URL in this render pass,
    // skip processing to avoid a spurious state reset / extra API call.
    if (pendingURLUpdateRef.current) {
      pendingURLUpdateRef.current = false;
      return;
    }

    // Skip if URL hasn't actually changed (prevents circular updates)
    if (lastProcessedURLRef.current === location.search) {
      return;
    }

    // Skip on initial mount - URL is already set
    if (isInitialMount.current) {
      return;
    }

    // Set flag to prevent circular updates
    isUpdatingFromURL.current = true;
    lastProcessedURLRef.current = location.search;

    // Deserialize URL parameters to filter state
    const newFilterState = deserializeFiltersFromURL();

    // Extract view_type from URL
    const urlViewType = searchParams.get("view_type");
    const newViewType: "execution" | "validation" =
      urlViewType === "validation" ? "validation" : "execution";

    // Update filter state (preserve additional fields not in config)
    setSearchFilterData((prev: any) => ({
      ...prev,
      ...newFilterState,
    }));

    // Update view type if it changed
    setPageTypeByTab((prev) => {
      if (prev.veIssues !== newViewType) {
        return { ...prev, veIssues: newViewType };
      }
      return prev;
    });

    // Reset flag after state update
    setTimeout(() => {
      isUpdatingFromURL.current = false;
    }, 0);
  }, [location.search, deserializeFiltersFromURL, searchParams]);

  // Ref to track the last processed URL for rqIssues tab
  const lastProcessedURLRQRef = useRef(location.search);

  // Ref to track whether the State→URL effect just updated the URL for rqIssues
  const pendingURLUpdateRQRef = useRef(false);

  // URL Synchronization: Update URL when filter state or view type changes (State → URL)
  // Only for rqIssues tab
  useEffect(() => {
    // Only sync URL for rqIssues tab
    if (selectedTab !== "rqIssues") {
      return;
    }

    // Skip URL update if we're currently updating from URL to prevent circular updates
    if (isUpdatingFromURLRQ.current) {
      return;
    }

    // Skip on initial mount - URL is already set
    if (isInitialMountRQ.current) {
      isInitialMountRQ.current = false;
      return;
    }

    // Serialize current filter state to URL parameters
    const params = serializeRQFiltersToURL(
      searchFilterData,
      pageTypeByTab.rqIssues
    );

    // Mark that we are about to update the URL so the URL→State effect
    // in the same render pass can skip processing it.
    pendingURLUpdateRQRef.current = true;

    // Update URL without adding to browser history (replace: true)
    setSearchParams(params, { replace: true });

    // Update the ref so URL→State effect won't re-process this URL change
    lastProcessedURLRQRef.current = `?${params.toString()}`;
  }, [
    searchFilterData,
    pageTypeByTab.rqIssues,
    selectedTab,
    serializeRQFiltersToURL,
    setSearchParams,
  ]);

  // URL Synchronization: Update filter state when URL changes (URL → State)
  // Only for rqIssues tab
  useEffect(() => {
    // Only sync URL for rqIssues tab
    if (selectedTab !== "rqIssues") {
      return;
    }

    // If the State→URL effect just updated the URL in this render pass,
    // skip processing to avoid a spurious state reset / extra API call.
    if (pendingURLUpdateRQRef.current) {
      pendingURLUpdateRQRef.current = false;
      return;
    }

    // Skip if URL hasn't actually changed (prevents circular updates)
    if (lastProcessedURLRQRef.current === location.search) {
      return;
    }

    // Skip on initial mount - URL is already set
    if (isInitialMountRQ.current) {
      return;
    }

    // Set flag to prevent circular updates
    isUpdatingFromURLRQ.current = true;
    lastProcessedURLRQRef.current = location.search;

    // Deserialize URL parameters to filter state
    const newFilterState = deserializeRQFiltersFromURL();

    // Extract view_type from URL
    const urlViewType = searchParams.get("view_type");
    const newViewType: "ruleResearch" | "genericResearch" =
      urlViewType === "genericResearch" ? "genericResearch" : "ruleResearch";

    // Update filter state (preserve additional fields not in config)
    setSearchFilterData((prev: any) => ({
      ...prev,
      ...newFilterState,
    }));

    // Update view type if it changed
    setPageTypeByTab((prev) => {
      if (prev.rqIssues !== newViewType) {
        return { ...prev, rqIssues: newViewType };
      }
      return prev;
    });

    // Reset flag after state update
    setTimeout(() => {
      isUpdatingFromURLRQ.current = false;
    }, 0);
  }, [location.search, selectedTab, deserializeRQFiltersFromURL, searchParams]);

  // Specific handlers for each tab type
  const handleExecutionTypeChange = useCallback(
    (newPageType: PageType) => {
      handlePageTypeChange(newPageType, "veIssues");
    },
    [handlePageTypeChange]
  );

  const handleRsQueryTypeChange = useCallback(
    (newPageType: PageType) => {
      handlePageTypeChange(newPageType, "rqIssues");
    },
    [handlePageTypeChange]
  );

  const renderDataGrid = () => {
    return (
      <IssueListDataGrid
        setUserDialogData={setUserDialogData}
        userDialogData={userDialogData}
        setIsLoading={setIsLoading}
        isLoading={isLoading}
        searchFilterData={searchFilterData}
        fetchedIssuesList={fetchedIssuesList}
        currentPageType={currentPageType}
        isShowEditIssue={isShowEditIssue}
        paginationMode="server"
        paginationModel={{
          page: page - 1,
          pageSize: pSize,
        }}
        onPaginationModelChange={(params: any) => {
          if (params.pageSize !== pSize || params.page !== page - 1) {
            setPage(params.page + 1);
            setPSize(params.pageSize);
          }
        }}
        rowCount={fetchedIssuesList?.total || 0}
        selectedIds={selectedIds}
        setSelectedIds={setSelectedIds}
      />
    );
  };

  const handleChange = (_event: React.SyntheticEvent, newValue: string) => {
    const newTab = newValue as TabType;
    setSelectedTab(newTab);

    // When switching tabs, sync the "last processed URL" ref for the target tab
    // to the current URL. This prevents the URL→State sync effects from
    // re-processing a URL that was written by the OTHER tab, which would
    // cause a spurious state update and an extra API call.
    if (newTab === "rqIssues") {
      lastProcessedURLRQRef.current = location.search;
    } else if (newTab === "veIssues") {
      lastProcessedURLRef.current = location.search;
    }
    //setMismatchedColumns([]);
    //setMissingColumns([]);
  };

  const tabContent: any = {
    veIssues: (
      <>
        <Box className="position-relative">
          <ButtonSwitcher
            buttonsName={["Execution", "Validation"]}
            selectedPlan={pageTypeByTab.veIssues}
            setSelectedPlan={handleExecutionTypeChange}
            className="issue-list-buttons"
          />
        </Box>

        <Box className="text-box-card no-radius list-page-card transaction-btn-group">
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              gap: "16px",
            }}
          >
            <Button className="btn-blue btn-orange btn-sm">
              <IconBoltType /> Action
            </Button>
            {isShowEditIssue && (
              <Button
                className="btn-blue btn-orange btn-sm"
                onClick={() => {
                  setUserDialogData((prev) => ({
                    ...prev,
                    status: true,
                    dialogName: "Update Issue",
                  }));
                }}
                disabled={userDialogData.selectedIds.length === 0}
              >
                Update Issue
              </Button>
            )}
          </Box>
          <SearchFilterBar
            className="styled"
            currentPageType={currentPageType}
            setSearchFilterData={updateSearchFilter}
            searchFilterData={searchFilterData}
            requiredFields={[
              "start_date",
              "end_date",
              "assigned_user",
              "assigned_by",
              "domain_list",
              // Show rule_list for execution view, resource_list for validation view
              pageTypeByTab.veIssues === "execution"
                ? "rule_list"
                : "resource_list",
              "execution_id",
              "status",
              "classification",
              "reason",
            ]}
            fieldRequirements={{
              includeDomainId: true,
              // Include rule_id for execution view, resource_id for validation view
              ...(pageTypeByTab.veIssues === "execution"
                ? { includeRuleId: true }
                : { includeResourceId: true }),
            }}
            primaryFields={["domain_list", "classification", "reason"]}
          />
        </Box>
        <Box sx={{ paddingTop: "20px" }}>{renderDataGrid()}</Box>
      </>
    ),
    rqIssues: (
      <>
        <Box className="position-relative">
          <ButtonSwitcher
            buttonsName={["Rule Research", "Generic Research"]}
            selectedPlan={pageTypeByTab.rqIssues}
            setSelectedPlan={handleRsQueryTypeChange}
            className="issue-list-buttons"
          />
        </Box>
        <Box className="text-box-card no-radius  list-page-card transaction-btn-group">
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              gap: "16px",
            }}
          >
            <Button className="btn-blue btn-orange btn-sm">
              <IconBoltType /> Action
            </Button>
            {isShowEditIssue && (
              <Button
                className="btn-blue btn-orange btn-sm"
                onClick={() => {
                  setUserDialogData((prev) => ({
                    ...prev,
                    status: true,
                    dialogName: "Update Issue",
                  }));
                }}
                disabled={userDialogData.selectedIds.length === 0}
              >
                Update Issue
              </Button>
            )}
          </Box>
          <SearchFilterBar
            className="styled"
            currentPageType={currentPageType}
            setSearchFilterData={updateSearchFilter}
            searchFilterData={searchFilterData}
            requiredFields={
              pageTypeByTab.rqIssues === "ruleResearch"
                ? [
                    // Rule Research view
                    "start_date",
                    "end_date",
                    "assigned_user",
                    "assigned_by",
                    "domain_list",
                    "rule_list",
                    "execution_id",
                    "status",
                  ]
                : [
                    // Generic Research view
                    "start_date",
                    "end_date",
                    "assigned_user",
                    "assigned_by",
                    "domain_list",
                    "execution_id",
                    "status",
                    "generic_research_id",
                    "query_name",
                  ]
            }
            fieldRequirements={{
              includeDomainId: true,
              // Include rule_id for Rule Research view
              ...(pageTypeByTab.rqIssues === "ruleResearch"
                ? { includeRuleId: true }
                : {}),
              placeholder:
                currentPageType === "ruleResearch"
                  ? "Rule Research Id Ex:1,2,3"
                  : "Generic Research Id Ex:1,2,3",
            }}
            primaryFields={
              pageTypeByTab.rqIssues === "ruleResearch"
                ? ["status", "domain_list"]
                : ["status", "generic_research_id", "query_name"]
            }
          />
        </Box>
        <Box sx={{ paddingTop: "20px" }}>{renderDataGrid()}</Box>
      </>
    ),
  };

  return (
    <>
      {isResultsLoading && <Loader isLoading={isResultsLoading} />}

      <Grid container className="tabs-with-butttons">
        <Grid item xs>
          <Tabs
            value={selectedTab}
            onChange={handleChange}
            textColor="secondary"
            indicatorColor="secondary"
            className="mui-tabs no-t-lr-radius min-height-0 alternative-1"
          >
            <Tab value="veIssues" label="Validation/Execution Issues" />
            <Tab value="rqIssues" label="Research Queries Issues" />
          </Tabs>
        </Grid>
      </Grid>

      <Box>{tabContent[selectedTab]}</Box>
    </>
  );
};

export default AllIssueList;
